import { Box, Button, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { blue, green, orange, purple, red, teal, yellow } from "@mui/material/colors";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DonutChart from "components/Dashboard/DonutChart";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { useMemo, useState } from "react";

const MAX_VISIBLE_ITEMS = 5;

export type TypeCountData = {
  type: string;
  count: number;
};

type WorkflowOverviewPieChartProps = {
  title: string;
  data?: TypeCountData[];
  loading: boolean;
  onItemClick?: (type: string) => void;
};

// Color palette matching the fulfillment dashboard style
const CHART_COLORS: Record<string, string> = {
  // Workflow types
  TierIIReportSubmission: blue[300],
  Cohort: purple[200],
  // Activity statuses
  InProgress: blue[300],
  Error: red[300],
  Complete: green[200],
  Pending: yellow[400],
  Canceled: purple[200],
  // Activity types - using a variety of colors
  Geppetto: teal[300],
  CreateSubmission: blue[400],
  PrepareForSubmission: blue[200],
  VerifySubmission: green[300],
  WaitForAllReportsToBeSubmitted: yellow[300],
  WaitForAllReportsToBeApproved: yellow[400],
  WaitForAllReportsToComplete: yellow[500],
  GenerateReport: orange[300],
  Manual: purple[300],
};

// Fallback colors for types not in the map
const FALLBACK_COLORS = [
  blue[300],
  green[200],
  yellow[400],
  red[300],
  purple[200],
  orange[300],
  teal[300],
  blue[100],
];

export function WorkflowOverviewPieChart({
  title,
  data,
  loading,
  onItemClick,
}: WorkflowOverviewPieChartProps) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  // Transform and sort data for the DonutChart component (sorted by count descending)
  const seriesData = useMemo(() => {
    if (!data) return [];

    return data
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count) // Sort by count descending
      .map((item, index) => ({
        id: item.type,
        value: item.count,
        label: prettyPrintEnumValue(item.type),
        color: CHART_COLORS[item.type] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length],
      }));
  }, [data]);

  // Calculate total for center label
  const total = useMemo(() => {
    return seriesData.reduce((sum, item) => sum + item.value, 0);
  }, [seriesData]);

  // Check if we need show more functionality
  const hasMoreItems = seriesData.length > MAX_VISIBLE_ITEMS;
  const hiddenItemsCount = seriesData.length - MAX_VISIBLE_ITEMS;

  // Handle click on chart item (both donut segment and legend)
  const handleItemClick = (type: string) => {
    if (onItemClick) {
      onItemClick(type);
    }
  };

  // Custom legend items showing counts (limited when collapsed)
  const legendItems = useMemo(() => {
    const itemsToShow = isExpanded ? seriesData : seriesData.slice(0, MAX_VISIBLE_ITEMS);
    
    const items = itemsToShow.map((item) => ({
      color: item.color,
      tooltip: `${item.label}: ${item.value.toLocaleString()}`,
      value: (
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          gap={1}
          sx={{ 
            overflow: "hidden",
            cursor: onItemClick ? "pointer" : "default",
            "&:hover": onItemClick ? {
              opacity: 0.8,
            } : {},
          }}
          onClick={() => handleItemClick(item.id as string)}
        >
          <Typography 
            variant="body2" 
            noWrap
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {item.label}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            {item.value.toLocaleString()}
          </Typography>
        </Box>
      ),
    }));

    // Add show more/less button as a legend item if needed
    if (hasMoreItems) {
      items.push({
        color: "transparent",
        tooltip: "",
        value: (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ 
              p: 0, 
              minWidth: "auto",
              textTransform: "none",
              fontSize: "0.75rem",
            }}
          >
            {isExpanded ? "Show less" : `+${hiddenItemsCount} more`}
          </Button>
        ),
      });
    }

    return items;
  }, [seriesData, isExpanded, hasMoreItems, hiddenItemsCount, onItemClick]);

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 2,
        height: "100%",
      }}
    >
      {loading ? (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              color: theme.palette.text.secondary,
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "calc(100% - 40px)",
              gap: 2,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 140,
                height: 140,
                flexShrink: 0,
              }}
            >
              <Skeleton
                variant="circular"
                width={140}
                height={140}
                sx={{
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
            </Box>
            <Stack spacing={1} sx={{ width: "100%" }}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    animation: `slideIn 0.3s ease-out ${index * 0.1}s both`,
                    "@keyframes slideIn": {
                      from: { opacity: 0, transform: "translateX(-10px)" },
                      to: { opacity: 1, transform: "translateX(0)" },
                    },
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={12}
                    height={12}
                    sx={{ borderRadius: "2px", flexShrink: 0 }}
                  />
                  <Skeleton variant="text" width="50%" height={18} />
                  <Skeleton variant="text" width="20%" height={18} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      ) : seriesData.length === 0 ? (
        <Box sx={{ height: "100%" }}>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              color: theme.palette.text.secondary,
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "calc(100% - 40px)",
            }}
          >
            <Typography variant="body2" color="text.disabled">
              No data available
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            height: "100%",
            animation: "fadeIn 0.5s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              color: theme.palette.text.secondary,
            }}
          >
            {title}
          </Typography>
          <DonutChart
            seriesData={seriesData}
            label={total.toLocaleString()}
            subLabel="Total"
            containerHeight="10rem"
            legend={{
              direction: "column",
              position: "right",
            }}
            legendItems={legendItems}
            legendSx={{
              minWidth: 120,
              maxWidth: 180,
              maxHeight: isExpanded ? "none" : "180px",
              overflowY: isExpanded ? "visible" : "hidden",
            }}
            showTooltipTitle={false}
            seriesOverrides={{
              innerRadius: 40,
              outerRadius: 70,
              ...(onItemClick && { 
                cx: "50%",
                cy: "50%",
              }),
            }}
            justifyContent="start"
            onItemClick={onItemClick ? (_event, pieItemId) => {
              const clickedItem = seriesData[pieItemId.dataIndex];
              if (clickedItem) {
                handleItemClick(clickedItem.id as string);
              }
            } : undefined}
          />
        </Box>
      )}
    </Paper>
  );
}
